<template>
  <div>
    <div class="flex items-center gap-4 px-4 py-3 border-b border-gray-100">
      <button type="button" :class="TEXT_BUTTON" @click="add">Add variant</button>
      <div v-if="dirty" class="ml-auto flex items-center gap-3">
        <span class="text-gray-600">Unsaved changes</span>
        <button type="button" :class="TEXT_BUTTON" @click="reset">Discard</button>
        <button type="button" :class="PRIMARY_BUTTON" :disabled="saving" @click="save">{{ saving ? 'Saving…' : 'Save' }}</button>
      </div>
    </div>
    <p v-if="saveError" class="px-4 pt-3 text-red-600">{{ saveError }}</p>
    <ul class="divide-y divide-gray-100">
      <li class="flex items-start gap-4 px-4 py-3">
        <div class="min-w-0 flex-1">
          <span class="font-medium text-gray-900">{{ DEPLOYED }}</span>
          <p class="mt-0.5 text-gray-600">What production runs today: {{ bench.defaultModel || 'the deployed model' }}, its system prompt, hats and tool instructions.</p>
        </div>
      </li>
      <li v-for="(v, i) in variants" :key="i" class="flex items-start gap-4 px-4 py-3">
        <div class="min-w-0 flex-1">
          <span class="font-medium text-gray-900">{{ v.label }}</span>
          <p class="mt-0.5 text-gray-600">{{ changes(v) }}</p>
          <p v-if="v.description" class="mt-0.5 text-gray-500">{{ v.description }}</p>
        </div>
        <div class="flex shrink-0 items-center gap-3 pt-0.5">
          <button type="button" :class="TEXT_BUTTON" @click="edit(i)">Edit</button>
          <button type="button" :class="TEXT_BUTTON" @click="remove(i)">Remove</button>
        </div>
      </li>
    </ul>

    <div v-if="editing !== null" class="fixed inset-0 z-40 bg-black/30 flex items-center justify-center" @click.self="editing = null">
      <div class="bg-white rounded-lg border border-gray-200 w-[60rem] max-w-[95vw] max-h-[90vh] flex flex-col">
        <div class="px-5 py-3 border-b border-gray-100 flex items-center">
          <h3 class="font-semibold text-gray-900">{{ draft.label || 'New variant' }}</h3>
          <button class="ml-auto text-gray-400 hover:text-gray-600" @click="editing = null">×</button>
        </div>
        <div class="p-5 overflow-y-auto grid grid-cols-12 gap-4">
          <label class="col-span-3"><span class="block text-gray-500 mb-2">Name</span><input v-model="draft.label" :class="INPUT" placeholder="prompt_v2"></label>
          <label class="col-span-9"><span class="block text-gray-500 mb-2">What it changes and why</span><input v-model="draft.description" :class="INPUT"></label>
          <label class="col-span-4">
            <span class="block text-gray-500 mb-2">Model</span>
            <select v-model="draft.model" :class="INPUT">
              <option value="">deployed ({{ bench.defaultModel || '—' }})</option>
              <option v-for="m in MODELS" :key="m" :value="m">{{ m }}</option>
            </select>
          </label>
          <label class="col-span-4">
            <span class="block text-gray-500 mb-2">Reasoning</span>
            <select v-model="draft.thinking" :class="INPUT">
              <option value="">deployed ({{ bench.defaultReasoning || '—' }})</option>
              <option v-for="r in ['low', 'medium', 'high']" :key="r" :value="r">{{ r }}</option>
            </select>
          </label>
          <label class="col-span-4">
            <span class="block text-gray-500 mb-2">Hats</span>
            <select v-model="draft.hats_enabled" :class="INPUT"><option value="">deployed (on)</option><option value="on">on</option><option value="off">off</option></select>
          </label>
          <label class="col-span-12">
            <span class="block text-gray-500 mb-2">System prompt: replaces tools/agent_prompt.md whole; leave empty to keep it</span>
            <textarea v-model="draft.system_prompt" :class="[INPUT, 'font-mono h-40']" />
          </label>
          <label class="col-span-6">
            <span class="block text-gray-500 mb-2">Hat edits (YAML): hat name → the fields that replace hats.yaml's; null removes the hat</span>
            <textarea v-model="draft.hats" :class="[INPUT, 'font-mono h-40']" placeholder="troubleshooter:&#10;  instructions: |-&#10;    ..." />
          </label>
          <label class="col-span-6">
            <span class="block text-gray-500 mb-2">Tool instructions (YAML): tool name → description and/or instructions</span>
            <textarea v-model="draft.tools" :class="[INPUT, 'font-mono h-40']" placeholder="search_tickets:&#10;  instructions: |-&#10;    ..." />
          </label>
        </div>
        <div class="px-5 py-3 border-t border-gray-100 flex items-center gap-3">
          <span v-if="draftError" class="text-red-600">{{ draftError }}</span>
          <button type="button" :class="[SECONDARY_BUTTON, 'ml-auto']" @click="editing = null">Cancel</button>
          <button type="button" :class="PRIMARY_BUTTON" @click="apply">Done</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { INPUT, PRIMARY_BUTTON, SECONDARY_BUTTON, TEXT_BUTTON, errorOf } from '../hermes';
import { DEPLOYED, saveVariants, variantFrom, variantsOf } from './bench';

export default {
  name: 'TestVariants',
  props: { bench: { type: Object, required: true } },
  emits: ['refresh'],
  data() {
    const variants = variantsOf(this.bench.template);
    return {
      variants, original: JSON.stringify(variants), saving: false, saveError: '', editing: null, draft: variantFrom({}), draftError: '',
      MODELS: ['claude-sonnet-5-5', 'claude-opus-5-5', 'gemini-3.8-flash'], DEPLOYED,
      INPUT, PRIMARY_BUTTON, SECONDARY_BUTTON, TEXT_BUTTON,
    };
  },
  computed: {
    dirty() {
      return JSON.stringify(this.variants) !== this.original;
    },
  },
  methods: {
    changes(v) {
      const bits = [];
      if (v.model) bits.push(v.model);
      if (v.thinking) bits.push(`reasoning ${v.thinking}`);
      if (v.hats_enabled) bits.push(`hats ${v.hats_enabled}`);
      if (v.system_prompt.trim()) bits.push('own system prompt');
      if (v.hats.trim()) bits.push('hat edits');
      if (v.tools.trim()) bits.push('tool instruction edits');
      return bits.join(' · ') || 'same as deployed';
    },
    add() {
      this.variants.push(variantFrom({ label: `variant_${this.variants.length + 1}` }));
      this.edit(this.variants.length - 1);
    },
    edit(i) {
      this.draft = { ...this.variants[i] };
      this.draftError = '';
      this.editing = i;
    },
    apply() {
      const v = variantFrom({ ...this.draft, label: this.draft.label.trim() });
      if (!/^[A-Za-z0-9_.-]{1,40}$/.test(v.label) || v.label === DEPLOYED) { this.draftError = `A short name: letters, digits, _ . - (not ${DEPLOYED}).`; return; }
      if (this.variants.some((x, i) => i !== this.editing && x.label === v.label)) { this.draftError = 'That name is taken.'; return; }
      this.variants.splice(this.editing, 1, v);
      this.editing = null;
    },
    remove(i) {
      this.variants.splice(i, 1);
    },
    reset() {
      this.variants = JSON.parse(this.original);
    },
    async save() {
      this.saving = true;
      this.saveError = '';
      try {
        await saveVariants(this.$axios, this.bench, this.variants);
        this.original = JSON.stringify(this.variants);
        this.$emit('refresh');
      } catch (e) {
        this.saveError = errorOf(e, 'The variants were not saved.');
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
